import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { fetchCampaignOptions } from "@/api/campaigns";
import {
  IMPACT_CAMPAIGN_PICKER_PAGE_SIZE,
  IMPACT_REPORT_MAX_FILTER_IDS,
} from "@/constants/impactReport";
import type { CampaignOptionsSort, CampaignOptionsStatusFilter } from "@/interfaces/campaing";

const BY_IDS_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * The campaign picker's paged list. `offset` is a row count, not a page number, so the next page
 * starts where the rows already held end. `pin` lists those campaigns first; the key sorts it so
 * the same set in a different order shares one cache entry. `hideUnredeemed` drops campaigns with
 * no redemptions, never a pinned one.
 */
export function useCampaignOptionsInfinite(
  params: {
    search: string;
    sort: CampaignOptionsSort;
    status: CampaignOptionsStatusFilter;
    pin: string[];
    hideUnredeemed: boolean;
  },
  enabled: boolean,
) {
  return useInfiniteQuery({
    queryKey: ["campaignOptions", "list", { ...params, pin: [...params.pin].sort() }],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      fetchCampaignOptions({
        ...params,
        search: params.search || undefined,
        pin: params.pin.length > 0 ? params.pin : undefined,
        offset: pageParam,
        limit: IMPACT_CAMPAIGN_PICKER_PAGE_SIZE,
      }),
    getNextPageParam: (last, _all, lastOffset) =>
      last.hasMore ? lastOffset + last.items.length : undefined,
    // Keeps the previous filter's rows on screen while the next one loads, so the list does not
    // flash empty between keystrokes.
    placeholderData: keepPreviousData,
    enabled,
  });
}

/**
 * Every campaign id the list shows for this search, status, pin and hideUnredeemed, for
 * select-all. Only meaningful when the list's total is at most IMPACT_REPORT_MAX_FILTER_IDS; past
 * that the result is a truncated page. The key sorts `pin` like the infinite hook's.
 */
export function useCampaignOptionMatchIds(
  params: {
    search: string;
    status: CampaignOptionsStatusFilter;
    pin: string[];
    hideUnredeemed: boolean;
  },
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["campaignOptions", "matchIds", { ...params, pin: [...params.pin].sort() }],
    queryFn: () =>
      fetchCampaignOptions({
        search: params.search || undefined,
        status: params.status,
        pin: params.pin.length > 0 ? params.pin : undefined,
        hideUnredeemed: params.hideUnredeemed,
        sort: "name",
        offset: 0,
        limit: IMPACT_REPORT_MAX_FILTER_IDS,
      }),
    select: (page) => page.items.map((o) => o.id),
    enabled,
  });
}

/**
 * Names and statuses for specific campaign ids (a selection or the recents), in request order.
 * The key sorts the ids so the same set in a different order shares one cache entry.
 */
export function useCampaignOptionsByIds(ids: string[]) {
  return useQuery({
    queryKey: ["campaignOptions", "ids", [...ids].sort()],
    enabled: ids.length > 0,
    queryFn: () => fetchCampaignOptions({ ids }),
    select: (page) => page.items,
    placeholderData: keepPreviousData,
    staleTime: BY_IDS_STALE_TIME_MS,
  });
}
